import api from './api';
import { 
  User, 
  CreateUserRequest, 
  UpdateUserRequest, 
  UserListResponse, 
  BulkUserAction,
  PasswordChangeRequest,
  Permission,
  UserPermissions,
  RolePermissions,
  UserActivity
} from '../interfaces/User';

export const userService = {
  async getUsers(page: number = 1, limit: number = 50): Promise<UserListResponse> {
    const response = await api.get(`/read/users?page=${page}&limit=${limit}`);
    if (response.data.data) {
      return {
        users: response.data.data,
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit
      };
    }
    return response.data;
  },

  async getUserById(id: number): Promise<User> {
    const response = await api.get(`/read/users/${id}`);
    return response.data;
  },

  async createUser(user: CreateUserRequest): Promise<User> {
    const response = await api.post('/write/users', user);
    return response.data;
  },

  async updateUser(id: number, user: UpdateUserRequest): Promise<User> {
    const response = await api.patch(`/write/users/${id}`, user);
    return response.data;
  },

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/write/users/${id}`);
  },

  async activateUser(id: number): Promise<User> {
    const response = await api.patch(`/write/users/${id}/activate`);
    return response.data;
  },

  async deactivateUser(id: number): Promise<User> {
    const response = await api.patch(`/write/users/${id}/deactivate`);
    return response.data;
  },

  async changeUserPassword(id: number, passwordData: PasswordChangeRequest): Promise<void> {
    await api.patch(`/write/users/${id}/password`, passwordData);
  },

  async resetUserPassword(id: number): Promise<{ temporary_password: string }> {
    const response = await api.post(`/write/users/${id}/reset-password`);
    return response.data;
  },

  async bulkAction(action: BulkUserAction): Promise<void> {
    await api.post('/write/users/bulk', action);
  },

  async searchUsers(query: string): Promise<User[]> {
    const response = await api.get(`/read/users/search?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  async getUserStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    by_role: Record<string, number>;
  }> {
    const response = await api.get('/read/users/stats');
    return response.data;
  },

  async validateEmail(email: string): Promise<{ available: boolean }> {
    const response = await api.get(`/read/users/validate-email?email=${encodeURIComponent(email)}`);
    return response.data;
  },

  async validateUsername(username: string): Promise<{ available: boolean }> {
    const response = await api.get(`/read/users/validate-username?username=${encodeURIComponent(username)}`);
    return response.data;
  },

  // Permission management
  async getAllPermissions(): Promise<Permission[]> {
    const response = await api.get('/admin/permissions');
    return response.data;
  },

  async getUserPermissions(userId: number): Promise<UserPermissions> {
    const response = await api.get(`/admin/users/${userId}/permissions`);
    return response.data;
  },

  async updateUserPermissions(userId: number, permissions: string[]): Promise<UserPermissions> {
    const response = await api.put(`/admin/users/${userId}/permissions`, { permissions });
    return response.data;
  },

  async getRolePermissions(role: string): Promise<RolePermissions> {
    const response = await api.get(`/admin/roles/${role}/permissions`);
    return response.data;
  },

  async updateRolePermissions(role: string, permissions: string[]): Promise<RolePermissions> {
    const response = await api.put(`/admin/roles/${role}/permissions`, { permissions });
    return response.data;
  },

  // User activity logs
  async getUserActivity(userId: number, limit: number = 50): Promise<UserActivity[]> {
    const response = await api.get(`/admin/users/${userId}/activity?limit=${limit}`);
    return response.data;
  },

  async getSystemActivity(limit: number = 100): Promise<UserActivity[]> {
    const response = await api.get(`/admin/activity?limit=${limit}`);
    return response.data;
  }
};